"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Eye, Plus, Search } from "lucide-react";
import { format } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getInvoices,
  GetInvoicesResponse,
  Invoice,
} from "@/api/broker/getInvoices";
import { BrokerInvoiceDialogContent } from "./BrokerInvoiceDialogContent";
import { InvoiceModalContent } from "./SelectedInvoiceModalContent";
import {
  updateInvoiceState,
  UpdateInvoiceStateParams,
} from "@/api/broker/updateInvoiceState";
import { getLoadsForInvoice } from "@/api/loads/getLoadsForInvoice";

export default function BrokerInvoicing() {
  const [searchTermReceived, setSearchTermReceived] = useState("");
  const [searchTermSent, setSearchTermSent] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const { updateStatus } = useUpdateInvoiceState();

  const { data: invoicesData } = useQuery({
    queryKey: ["broker", "invoices"],
    queryFn: () => getInvoices(),
  });

  const { data: loadsToInvoice } = useQuery({
    queryKey: ["broker", "invoices", "loadsToInvoice"],
    queryFn: () => getLoadsForInvoice({ invoiceId: null }),
    enabled: !!createDialogOpen,
  });

  const receivedInvoices =
    invoicesData?.receivedInvoices.sort(
      (a, b) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
    ) || [];

  const sentInvoices =
    invoicesData?.sentInvoices.sort(
      (a, b) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
    ) || [];

  const handleStatusChange = async (invoiceId: string, newStatus: string) => {
    await updateStatus({ invoiceId, newStatus });
  };

  function highlightMatch(text: string, query: string) {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  }

  const generatePDF = (invoice: any) => {
    console.log("Generating PDF for invoice:", invoice);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      Paid: "bg-green-500",
      Pending: "bg-lupurple",
      Overdue: "bg-orange-300",
    } as const;

    return (
      <Badge
        className={variants[status as keyof typeof variants] || "bg-slate-400"}
      >
        {status}
      </Badge>
    );
  };

  return (
    <div className=" mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Invoicing</h1>
          <p className="text-muted-foreground">
            Manage your invoices and billing
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>

          <BrokerInvoiceDialogContent
            loads={loadsToInvoice ?? []}
            setDialogOpen={setCreateDialogOpen}
          />
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Accounts Receivable</CardTitle>
          <CardDescription>Money that you're owed</CardDescription>
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search invoices..."
              value={searchTermReceived}
              onChange={(e) => setSearchTermReceived(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice ID</TableHead>
                <TableHead>Sent To</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sentInvoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className="font-medium">
                    {highlightMatch(invoice.name, searchTermReceived)}
                  </TableCell>
                  <TableCell>
                    {highlightMatch(invoice.otherPartyName, searchTermReceived)}
                  </TableCell>

                  <TableCell>{format(invoice.createdAt, "MM-dd-yy")}</TableCell>
                  <TableCell>{format(invoice.dueDate, "MM-dd-yy")}</TableCell>
                  <TableCell>
                    ${(invoice.amountCents / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setViewDialogOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => generatePDF(invoice)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Select
                        value={invoice.status}
                        onValueChange={(value) =>
                          handleStatusChange(invoice.id, value)
                        }
                      >
                        <SelectTrigger className="w-24 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Overdue">Overdue</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        {selectedInvoice && (
          <InvoiceModalContent
            invoice={selectedInvoice}
            setViewDialogOpen={setViewDialogOpen}
            generatePDF={generatePDF}
            isSent={selectedInvoice.otherPartyName !== "Saia"}
          />
        )}
      </Dialog>
      <Card>
        <CardHeader>
          <CardTitle>Accounts Payable</CardTitle>
          <CardDescription>Invoices that you need to pay</CardDescription>
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search invoices..."
              value={searchTermSent}
              onChange={(e) => setSearchTermSent(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice ID</TableHead>
                <TableHead>Sent From</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receivedInvoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className="font-medium">
                    {highlightMatch(invoice.name, searchTermSent)}
                  </TableCell>
                  <TableCell>
                    {highlightMatch(invoice.otherPartyName, searchTermSent)}
                  </TableCell>

                  <TableCell>{format(invoice.createdAt, "MM-dd-yy")}</TableCell>
                  <TableCell>{format(invoice.dueDate, "MM-dd-yy")}</TableCell>
                  <TableCell>
                    ${(invoice.amountCents / 100).toFixed(2)}
                  </TableCell>
                  <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setViewDialogOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => generatePDF(invoice)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Select
                        value={invoice.status}
                        onValueChange={(value) =>
                          handleStatusChange(invoice.id, value)
                        }
                      >
                        <SelectTrigger className="w-24 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Overdue">Overdue</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export const useUpdateInvoiceState = () => {
  const queryClient = useQueryClient();

  const updateStatus = async ({
    invoiceId,
    newStatus,
  }: {
    invoiceId: string;
    newStatus: string;
  }) => {
    const queryKey = ["broker", "invoices"];

    // Cancel any outgoing refetches so they don't overwrite our optimistic update
    //await queryClient.cancelQueries({ queryKey });

    const previousData =
      queryClient.getQueryData<GetInvoicesResponse[]>(queryKey);

    queryClient.setQueryData<GetInvoicesResponse>(queryKey, (old) => {
      if (!old) return old;

      return {
        sentInvoices: old.sentInvoices.map((invoice) =>
          invoice.id === invoiceId ? { ...invoice, state: newStatus } : invoice
        ),
        receivedInvoices: old.receivedInvoices.map((invoice) =>
          invoice.id === invoiceId ? { ...invoice, state: newStatus } : invoice
        ),
      };
    });

    try {
      // Await the actual mutation
      await updateInvoiceState({
        invoiceId,
        state: newStatus as UpdateInvoiceStateParams["state"],
      });
    } catch (error) {
      // Rollback on error
      queryClient.setQueryData(queryKey, previousData);
      throw error;
    } finally {
      // Optional: revalidate to get the latest server data
      queryClient.invalidateQueries({ queryKey });
    }
  };

  return { updateStatus };
};
